import React from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { Minus, TrendingDown, TrendingUp } from "lucide-react";

import { formatDate } from "utils/dateFormatter";

import s from "./BaselineTrendChart.module.css";

const DIRECTION_ICON = { up: TrendingUp, down: TrendingDown, flat: Minus };

const TrendIndicator = ({ indicator, unit }) => {
  const Icon = DIRECTION_ICON[indicator.direction];
  const sign = indicator.delta > 0 ? "+" : "";

  return (
    <span className={s.indicator}>
      <span className={s.indicatorValue}>
        <Icon size={16} strokeWidth={2} />
        {sign}
        {indicator.delta.toFixed(1)} {unit}
      </span>
      <span className={s.indicatorDates}>
        {formatDate(indicator.previousDate)} → {formatDate(indicator.currentDate)}
      </span>
    </span>
  );
};

// One reusable Highcharts trend panel, driven entirely by props — see
// utils/baselineTrends.js for how `series`/`indicator` are built from a
// patient's baseline history. `series[].data` is expected pre-filtered
// (no 0/null/empty points) by the caller.
const BaselineTrendChart = ({ title, unit, series, indicator, isLoading }) => {
  const hasData = series.some((oneSeries) => oneSeries.data.length > 0);

  return (
    <div className={s.chartCard}>
      <div className={s.header}>
        <h3 className={s.title}>{title}</h3>
        {!!indicator && <TrendIndicator indicator={indicator} unit={unit} />}
      </div>

      {!!isLoading && <p className={s.emptyMessage}>Loading...</p>}

      {!isLoading && hasData && (
        <HighchartsReact
          highcharts={Highcharts}
          options={{
            chart: { type: "line", height: 280 },
            title: { text: null },
            credits: { enabled: false },
            xAxis: { type: "datetime", title: { text: "Date" } },
            yAxis: { title: { text: unit } },
            series,
            legend: { enabled: series.length > 1 },
            tooltip: {
              xDateFormat: "%d/%m/%Y",
              valueSuffix: ` ${unit}`,
            },
          }}
        />
      )}

      {!isLoading && !hasData && (
        <p className={s.emptyMessage}>No readings recorded yet</p>
      )}
    </div>
  );
};

export { BaselineTrendChart };
